import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { AuthShell } from "../components/layout/AuthShell";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { useAuth } from "../lib/auth";

const LAST_EMAIL = "wise.last-email";

/** Sign in / sign up. We capture the email here and pass it to the identity
 *  provider as a login hint, so the credential step arrives pre-filled. */
export default function Login() {
  const { user, loading, unavailable, login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem(LAST_EMAIL) ?? "");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8 text-brand-500" />
      </div>
    );
  }
  if (user) return <Navigate to="/app" replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    localStorage.setItem(LAST_EMAIL, value);
    setBusy(true);
    login(value);
  }

  return (
    <AuthShell
      title="Sign in to Wise 2.0"
      subtitle="Enter your work email to continue. New here? The same step creates your account."
    >
      {unavailable ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-semibold text-amber-900">Sign-in isn't configured yet</p>
          <p className="mt-1.5 text-sm text-amber-800">
            This deployment is missing its identity settings. Ask an administrator to configure the{" "}
            <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">B2C_*</code> values in the API.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Work email" htmlFor="email">
            <Input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Button type="submit" size="lg" className="group w-full" loading={busy} disabled={!email.trim()}>
            Continue
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>

          <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-500">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
            Your credentials are verified on a secure sign-in page and never touch Wise 2.0. Multi-factor
            authentication is applied where your organization requires it.
          </p>
        </form>
      )}

      <p className="mt-8 text-sm text-slate-500">
        <Link to="/" className="font-medium text-brand-700 hover:underline">
          ← Back to site
        </Link>
      </p>
    </AuthShell>
  );
}
